import {
  Button,
  Cog6ToothIcon,
  Collapsible,
  Drawer,
  DrawerProps,
  Flex,
  InputContainer,
  NumberInput,
  SimpleGrid,
  SingleDateSelect,
  Switch,
  Text,
  ToggleButton,
} from 'components'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import dayjs from 'dayjs'
import { QueryConstraint, where } from 'firebase/firestore'
import { isEqual, startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import {
  CollectionName,
  CountByLimitedResource,
  IWorkspaceSubscription,
  LimitedResource,
  OmitDBProps,
} from 'types'
import {
  createTimestamp,
  getCount,
  mergeObjects,
  resourceLimitsByPlan,
  setDoc,
  splitDBProps,
} from 'utils'

interface Props extends Omit<DrawerProps, 'children'> {}

export default function ManageSubscriptionDrawer({ open, onClose, ...rest }: Props) {
  const { confirm } = useAlert()
  const user = useCurrentUser()
  const { subscription, workspace } = useWorkspace()

  const [_, { countByResource, ...subscriptionProps }] = splitDBProps(subscription)
  const initialData = mergeObjects(defaultSubscription, subscriptionProps)
  const [draft, setDraft] = useState(initialData)
  const {
    billingFrequency,
    isTrial,
    limitsResetDate,
    nextPaymentDueDate,
    plan,
    selectedResource,
    addOnByResource,
  } = draft
  const [matchDates, setMatchDates] = useState(limitsResetDate === nextPaymentDueDate)

  const dirty = !isEqual(initialData, draft)
  const lockDates = matchDates && billingFrequency === 'yearly'
  const resourceLimits = resourceLimitsByPlan[plan]

  useEffect(() => {
    if (!open) return

    setDraft(initialData)
    setMatchDates(initialData.limitsResetDate === initialData.nextPaymentDueDate)
  }, [open, workspace])

  useEffect(() => {
    if (matchDates && nextPaymentDueDate !== limitsResetDate) setMatchDates(false)
  }, [nextPaymentDueDate, limitsResetDate])

  async function handleDone() {
    if (!dirty) return onClose?.()

    const { isTrial, selectedResource, addOnByResource, ...requiredProps } = draft
    const nonZeroAddOnByResource = Object.fromEntries(
      Object.entries(addOnByResource).filter(([_, value]) => value > 0)
    ) as IWorkspaceSubscription['addOnByResource']

    const body = {
      ...requiredProps,
      addOnByResource: plan === 'enterprise' ? {} : nonZeroAddOnByResource,
      ...(plan === 'basic' && { selectedResource }),
      ...(isTrial && { isTrial: true }),
    }

    let countByResource = subscription?.countByResource
    if (!countByResource || draft.limitsResetDate !== initialData.limitsResetDate)
      countByResource = await getCountByResource()

    await setDoc(user!.id, 'workspaceSubscriptions', {
      id: workspace!.id,
      countByResource,
      ...body,
    })

    onClose?.()
  }

  async function getCountByResource() {
    const makeQuery = (collection: CollectionName, constraints?: QueryConstraint[]) =>
      getCount(collection, where('workspaceId', '==', workspace!.id), ...(constraints || []))

    const resetDate = draft.limitsResetDate
    const limitStartDate = dayjs(resetDate).subtract(1, 'year').valueOf()
    const eventConstraints = [
      where('startTime', '>=', limitStartDate),
      where('startTime', '<=', resetDate),
    ]

    const queries: Array<{
      name: keyof Omit<CountByLimitedResource, 'users'>
      getCount: Promise<number>
    }> = [
      { name: 'events', getCount: makeQuery('events', eventConstraints) },
      { name: 'facilities', getCount: makeQuery('facilities') },
      { name: 'templates', getCount: makeQuery('templates') },
    ]

    return Promise.all(queries.map(q => q.getCount)).then(counts => {
      return counts.reduce(
        (acc, count, index) => {
          acc[queries[index].name] = count
          return acc
        },
        {} as Omit<CountByLimitedResource, 'users'>
      )
    })
  }

  async function handleCancel() {
    if (dirty) {
      const response = await confirm('Discard changes?', {
        cancelText: 'Cancel',
        okText: 'Discard',
      })
      if (!response) return
    }

    onClose?.()
  }

  return (
    <Drawer
      bodyProps={{ gap: 6, alignItems: 'flex-start' }}
      headerIcon={<Cog6ToothIcon />}
      header="Manage Subscription"
      footer={
        <>
          {dirty && (
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button palette={dirty ? 'primary' : undefined} onClick={handleDone}>
            {dirty ? 'Save' : 'Done'}
          </Button>
        </>
      }
      open={open}
      onClose={handleCancel}
      {...rest}
    >
      <ToggleButton
        label="Billing Frequency"
        value={billingFrequency}
        options={[
          { label: 'Month', value: 'monthly' },
          { label: 'Annual', value: 'yearly' },
        ]}
        onChange={billingFrequency => setDraft({ ...draft, billingFrequency })}
        containerProps={containerProps}
      />
      <Switch
        label="Trial plan"
        checked={isTrial}
        onChange={isTrial => setDraft({ ...draft, isTrial })}
      />
      <ToggleButton
        label="Plan"
        value={plan}
        options={[
          { label: 'Basic', value: 'basic' },
          { label: 'Pro', value: 'pro' },
          { label: 'Enterprise', value: 'enterprise' },
        ]}
        onChange={plan => setDraft({ ...draft, plan })}
        containerProps={containerProps}
      />
      <Collapsible open={plan === 'basic'}>
        <ToggleButton
          label="Selected Resource"
          value={selectedResource}
          options={[
            { label: 'Events', value: 'event' },
            { label: 'Facilities', value: 'facility' },
          ]}
          onChange={selectedResource => setDraft({ ...draft, selectedResource })}
          containerProps={containerProps}
        />
      </Collapsible>
      <SingleDateSelect
        buttonDisplayProps={{ color: 'black' }}
        label="Next Payment Due Date"
        selected={nextPaymentDueDate}
        onSelect={date => {
          if (!date) return

          setDraft({
            ...draft,
            nextPaymentDueDate: date,
            ...(lockDates && { limitsResetDate: date }),
          })
        }}
      />

      <Collapsible open={billingFrequency === 'yearly'}>
        <Switch
          label="Reset limits on due date"
          checked={matchDates}
          onChange={checked => {
            setMatchDates(checked)
            if (checked) setDraft({ ...draft, limitsResetDate: nextPaymentDueDate })
          }}
        />
      </Collapsible>
      <Collapsible open={!lockDates}>
        <SingleDateSelect
          label="Limits Reset Date"
          selected={limitsResetDate}
          onSelect={date => {
            if (!date) return
            setDraft({ ...draft, limitsResetDate: date })
          }}
        />
      </Collapsible>
      <Collapsible open={plan !== 'enterprise'}>
        <InputContainer label="Add Ons">
          <SimpleGrid columns={2} gapX={12} gapY={4}>
            {(Object.entries(addOnByResource) as Array<[LimitedResource, number]>).map(
              ([key, value]) => (
                <InputContainer
                  key={key}
                  label={
                    <Text textStyle="bodyXSmall" opacity={0.7}>
                      {startCase(key)}
                    </Text>
                  }
                >
                  <Flex alignItems="center" gap={2}>
                    <NumberInput
                      value={value}
                      min={0}
                      onChange={v =>
                        setDraft({ ...draft, addOnByResource: { ...addOnByResource, [key]: v } })
                      }
                    />
                    <Text textStyle="bodySmall" whiteSpace="nowrap">
                      {resourceLimits[key] + value} total
                    </Text>
                  </Flex>
                </InputContainer>
              )
            )}
          </SimpleGrid>
        </InputContainer>
      </Collapsible>
    </Drawer>
  )
}

const nextYear = dayjs(createTimestamp()).add(1, 'year').valueOf()

type EditableSubscription = Required<OmitDBProps<Omit<IWorkspaceSubscription, 'countByResource'>>>

const defaultSubscription: EditableSubscription = {
  billingFrequency: 'yearly',
  isTrial: false,
  limitsResetDate: nextYear,
  nextPaymentDueDate: nextYear,
  plan: 'enterprise',
  selectedResource: 'event',
  addOnByResource: {
    events: 0,
    facilities: 0,
    templates: 0,
    users: 0,
  },
}

const containerProps = {
  backgroundColor: 'white',
}
